import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Download, Pencil, CheckCircle2 } from 'lucide-react';
import { rb, RBField, RBInput } from '../theme';
import ATSPanel from '../ATSPanel';

export default function ReviewStep({ state, onEditStep, onGeneratePdf }) {
  const [title, setTitle] = useState('My Resume');
  const [generating, setGenerating] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(null);

  const generate = async () => {
    if (generating) return;
    setGenerating(true);
    setError(null);
    try {
      await onGeneratePdf(title.trim() || 'My Resume');
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || "Couldn't generate the PDF. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  const filledExp = state.experiences.filter((e) => e.title || e.company).length;
  const filledEdu = state.educations.filter((e) => e.degree || e.institution).length;

  const summaryRows = [
    { step: 0, label: 'Personal details', value: state.profile.name ? `${state.profile.name} · ${state.profile.phone}` : 'Not filled' },
    { step: 1, label: 'Work experience', value: filledExp ? `${filledExp} job(s)` : 'None added' },
    { step: 2, label: 'Education', value: filledEdu ? `${filledEdu} qualification(s)` : 'None added' },
    { step: 3, label: 'Skills', value: state.skills.length ? state.skills.slice(0, 5).join(', ') + (state.skills.length > 5 ? '…' : '') : 'None added' },
    { step: 4, label: 'Extras', value: [state.projects.length && 'projects', state.certifications.length && 'certificates', state.languages.length && 'languages', state.achievements.length && 'achievements'].filter(Boolean).join(', ') || 'None added' },
  ];

  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-400">
        Almost done! Check everything looks right, then download your resume as a PDF.
      </p>

      <div className={`rounded-2xl divide-y divide-white/10 ${rb.glass}`}>
        {summaryRows.map((row) => (
          <div key={row.label} className="flex items-center justify-between gap-3 px-4 py-3">
            <div className="min-w-0">
              <p className="text-xs font-bold uppercase tracking-wide text-slate-400">{row.label}</p>
              <p className="text-sm truncate text-slate-100">{row.value}</p>
            </div>
            <button type="button" onClick={() => onEditStep(row.step)} className={`inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1.5 rounded-lg shrink-0 ${rb.secondaryBtn}`}>
              <Pencil className="w-3 h-3" /> Edit
            </button>
          </div>
        ))}
      </div>

      <RBField label="Resume name">
        <RBInput value={title} onChange={(e) => setTitle(e.target.value)} placeholder="My Resume" />
      </RBField>

      {error && <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/40 text-red-300 text-sm">{error}</div>}

      <button type="button" onClick={generate} disabled={generating} className={`w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl text-base ${rb.glowBtn}`}>
        {generating ? <Loader2 className="w-5 h-5 animate-spin" /> : <Download className="w-5 h-5" />}
        {generating ? 'Creating your PDF…' : done ? 'Download again' : 'Generate & Download PDF'}
      </button>

      <AnimatePresence>
        {done && !generating && (
          <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-center gap-2 justify-center text-sm font-semibold text-emerald-400">
            <CheckCircle2 className="w-4 h-4" /> Resume downloaded! Best of luck with your applications.
          </motion.div>
        )}
      </AnimatePresence>

      <ATSPanel />
    </div>
  );
}
